import { organizations, adminUnits, contactPersons } from './itemsFixtureData';

/**
 * DL-65655 (epic — dl-58221 part 2, "regression on existing columns & every custom field type").
 * Real, persisted Items rows that make the server-side sort falsifiable on a small, deterministic
 * subset rather than the noisy full generated dataset (whose supplier column, for one, is
 * interleaved with empty-supplier probe rows from other epics — the empty `_` placeholder then
 * breaks a naive string-monotonic order check). Same rationale and shape as part 1's
 * `itemsSortingProbeFixtureData.ts`: REAL rows (not an `apiTransform` GET override, which would
 * bypass `sortItems` and only prove a hardcoded order), each carrying a unique name token so a spec
 * isolates exactly its four with the mock's free-text name search.
 *
 * Two disjoint row sets so Half 1 (existing columns) and Half 2 (custom-field types) never share a
 * fixture: the CF-valued rows carry a populated `customFields[]`, which changes the Items table's
 * horizontal virtualization enough to push the far-right Ready-for-export column out of the DOM —
 * so the standard-column specs use the CF-free `zzRegProbe` rows, and only the CF-type spec uses the
 * `zzCfProbe` rows.
 */

let probeId = 943200;

// ── Half 1: existing standard/boolean/date columns (CF-free `zzRegProbe` rows) ────────────────────
//
// | suffix | name    | userDefinedId | brand         | supplier      | isActive | readyForExport | lastUpdated |
// |--------|---------|---------------|---------------|---------------|----------|----------------|-------------|
// | Delta  | Delta   | IT-ZR-40      | Delta Brand   | Yak Supplier  | false    | true           | 05-Feb-2025 |
// | Alpha  | Alpha   | IT-ZR-10      | Alpha Brand   | Bee Supplier  | true     | false          | 15-Mar-2025 |
// | Charlie| Charlie | IT-ZR-30      | Charlie Brand | Cod Supplier  | false    | true           | 20-Apr-2025 |
// | Bravo  | Bravo   | IT-ZR-20      | Bravo Brand   | Ant Supplier  | true     | false          | 10-Jan-2025 |
//
// Values are deliberately non-alphabetical and the lastUpdated dates are out of name order, so every
// column's sort is falsifiable rather than coinciding with the default name/ASC order. Booleans sort
// false-first in ASC (`compareValues` in `items.ts`) — Inactive before Active, No before Yes —
// settling the "sorted consistently" wording flagged for Status (753689) and Ready for export
// (753690).

type RegProbeOverrides = {
  name: string;
  userDefinedId: string;
  brandName: string;
  supplierName: string;
  isActive: boolean;
  readyForExport: boolean;
  lastUpdated: string;
};

/**
 * A stored custom-field value entry. Every field present and typed to match the canonical
 * `ItemCustomFieldEntry` shape (`itemsClearFieldFixtures.ts`) that other rows already contribute to
 * `itemsData` — a partial/narrower shape here breaks the array-spread assignability check in
 * `mergeCustomFields` below (pre-existing code), which every fixture file's `customFields` shares
 * as one implicit structural union.
 */
const cf = (customFieldId: number, name: string, type: number, value: string | null) => ({
  id: customFieldId * 1000 + (probeId % 1000),
  customFieldId,
  name,
  userDefinedId: null as string | null,
  type,
  value,
  itemCustomFieldAllowedValueId: null as number | null,
  customFieldAllowedValueUserDefinedID: null as string | null,
  isMandatory: false,
  isSupplierEditable: false,
  internalOnly: false,
  maxChar: null as number | null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null as number | null,
  parentCustomFieldName: null as string | null,
});

// Narrowly typed (not `unknown[]`) so this fixture's `customFields` entries keep the concrete
// shape `cf()` produces — a widened type here leaks into `itemsData`'s inferred element union and
// poisons unrelated, pre-existing code elsewhere in items.ts (`mergeCustomFields`'s
// `cf.customFieldId`, the `readApiTransform`/audit-log readers) that has nothing to do with this
// fixture. Every fixture file contributing to `itemsData` must keep its `customFields` shape this
// concrete for the same reason (see the same fix on `itemsCfTypeSortingProbeFixtureData.ts`, now
// folded into this file's `zzCfProbe` rows).
const baseRow = (o: {
  name: string;
  userDefinedId: string;
  brandName: string;
  supplierName: string;
  isActive: boolean;
  readyForExport: boolean;
  lastUpdated: string;
  customFields: ReturnType<typeof cf>[];
}) => ({
  id: probeId++,
  name: o.name,
  userDefinedId: o.userDefinedId,
  description: null,
  brand: { id: probeId + 5000, name: o.brandName },
  organization: organizations[0],
  supplier: { id: probeId + 6000, name: o.supplierName },
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: o.isActive,
  suppliedBy: null,
  readyForExport: o.readyForExport,
  updatedAfterLastExport: false,
  lastUpdated: o.lastUpdated,
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 7,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: o.customFields,
});

const regProbe = (o: RegProbeOverrides) => baseRow({ ...o, customFields: [] });

export const itemsSortingRegressionProbeRows = [
  regProbe({
    name: 'zzRegProbe Delta',
    userDefinedId: 'IT-ZR-40',
    brandName: 'Delta Brand',
    supplierName: 'Yak Supplier',
    isActive: false,
    readyForExport: true,
    lastUpdated: '2025-02-05T09:00:00',
  }),
  regProbe({
    name: 'zzRegProbe Alpha',
    userDefinedId: 'IT-ZR-10',
    brandName: 'Alpha Brand',
    supplierName: 'Bee Supplier',
    isActive: true,
    readyForExport: false,
    lastUpdated: '2025-03-15T09:00:00',
  }),
  regProbe({
    name: 'zzRegProbe Charlie',
    userDefinedId: 'IT-ZR-30',
    brandName: 'Charlie Brand',
    supplierName: 'Cod Supplier',
    isActive: false,
    readyForExport: true,
    lastUpdated: '2025-04-20T09:00:00',
  }),
  regProbe({
    name: 'zzRegProbe Bravo',
    userDefinedId: 'IT-ZR-20',
    brandName: 'Bravo Brand',
    supplierName: 'Ant Supplier',
    isActive: true,
    readyForExport: false,
    lastUpdated: '2025-01-10T09:00:00',
  }),
];

// ── Half 2: custom-field-type columns (CF-valued `zzCfProbe` rows) ────────────────────────────────
//
// Distinct Text (cf 9 "Internal notes"), Date (cf 11 "Launch date") and Numeric (cf 13 "Target
// weight") values, deliberately out of name order. The Numeric values (90/200/1000/1500) also guard
// numeric-not-lexicographic ordering. The Items mock resolves the composed sort key
// `cf_{id}_{type}` to these values (getSortValue, items.ts).

const cfProbe = (o: {
  name: string;
  userDefinedId: string;
  cfText: string;
  cfDate: string;
  cfNumber: string;
}) =>
  baseRow({
    name: o.name,
    userDefinedId: o.userDefinedId,
    brandName: 'CF Brand',
    supplierName: 'CF Supplier',
    isActive: true,
    readyForExport: false,
    lastUpdated: '2025-06-15T09:00:00',
    customFields: [
      cf(9, 'Internal notes', 2, o.cfText),
      cf(11, 'Launch date', 1, o.cfDate),
      cf(13, 'Target weight', 4, o.cfNumber),
    ],
  });

export const itemsSortingCfProbeRows = [
  cfProbe({
    name: 'zzCfProbe Delta',
    userDefinedId: 'IT-ZC-40',
    cfText: 'Zephyr note',
    cfDate: '2025-08-01T00:00:00',
    cfNumber: '1500',
  }),
  cfProbe({
    name: 'zzCfProbe Alpha',
    userDefinedId: 'IT-ZC-10',
    cfText: 'Mango note',
    cfDate: '2025-05-01T00:00:00',
    cfNumber: '200',
  }),
  cfProbe({
    name: 'zzCfProbe Charlie',
    userDefinedId: 'IT-ZC-30',
    cfText: 'Apple note',
    cfDate: '2025-11-01T00:00:00',
    cfNumber: '90',
  }),
  cfProbe({
    name: 'zzCfProbe Bravo',
    userDefinedId: 'IT-ZC-20',
    cfText: 'Kiwi note',
    cfDate: '2025-02-01T00:00:00',
    cfNumber: '1000',
  }),
];
